<template>
	<div class="d-flex" :style="{ width: display.smAndDown.value ? 'unset' : 'fit-content' }">
		<!-- LSP-16462: the stacked, mobile media layout starts at `smAndDown` (< 960px) in
		     EntityDetailPage, so the filter funnel and the full-width field must use the same
		     boundary. Vuetify's `display.mobile` is configured as `mobileBreakpoint: "sm"`
		     (< 600px), which left iPad portrait on the mobile layout with no way to open the
		     media filters. -->
		<BaseSearchField
			ref="searchInput"
			v-model="searchTerm"
			v-test="'results-search'"
			:min-width="240"
			:append-icon="showFilters && display.smAndDown.value ? 'icon-filter-solid' : undefined"
			:placeholder="placeholder"
			class="flex-grow-1"
			@input="onSearchInput"
			@input.native="handleSearchInput"
			@blur="sanitize"
			@focus="sanitize"
			@paste="onSearchPaste"
			@click:append="toggleFilters"
			@keydown="handleSearchKeyDown"
			@keyup.enter="onImmediateSearch($event)" />
	</div>
</template>
<script setup lang="ts">
	import { isSearchTooLong } from "@/helpers/searchLimit";
	import BaseSearchField from "@/components/formfields/BaseSearchField.vue";
	import { useSearchQuery } from "@/composables/useDebouncedSearch";
	import { cleanSearchTerm, handleSearchPaste, handleSearchKeyDown, handleSearchInput, dismissSoftKeyboard } from "@/helpers/filters";
	import { calculateTimeout } from "@/helpers/utilities";
	import { getCurrentInstance, nextTick, ref, watch } from "vue";
	import { useDisplay } from "vuetify";
	import { useToast } from "vue-toastification";

	const display = useDisplay();
	const props = withDefaults(
		defineProps<{
			searchCallback: (searchTerm: string) => void;
			showFilters: boolean;
			modelValue?: string;
			placeholder?: string;
		}>(),
		{
			placeholder: "Search"
		}
	);

	const filtersVisible = ref(false);
	const searchInput = ref<HTMLInputElement | null>(null);
	const instance = getCurrentInstance();
	const toast = useToast();

	const { searchTerm, inputChanged, search, setValueSilent, cancel } = useSearchQuery({
		debounceMs: 500,
		searchCallback: props.searchCallback
	});

	const emits = defineEmits<{
		(e: "toggleFilters", filtersVisible: boolean): void;
		(e: "update:modelValue", value: string): void;
	}>();

	watch(
		() => props.modelValue,
		(newValue) => {
			const next = newValue ?? "";

			if (next !== (searchTerm.value ?? "")) {
				setValueSilent(next);
			}
		},
		{ immediate: true }
	);

	watch(searchTerm, (newValue) => {
		emits("update:modelValue", newValue ?? "");
	});

	function onSearchInput() {
		sanitize();
		search(searchTerm.value ?? "");
	}

	/**
	 * Cancels the search debounce and triggers an immediate search.
	 * Blurs the originating input first so the mobile soft keyboard dismisses (LSP-15898).
	 */
	function onImmediateSearch(event?: Event) {
		cancel();
		sanitize();
		dismissSoftKeyboard(event, searchInput.value as any);
		if (isSearchTooLong(searchTerm.value)) return;
		props.searchCallback(searchTerm.value ?? "");
	}

	/**
	 * Sanitize the search term and update the input value. This is to handle emojis and other special characters not entered using the keyboard.
	 */
	function sanitize() {
		const cleaned = cleanSearchTerm(searchTerm.value);
		searchTerm.value = cleaned;

		nextTick(() => {
			if (searchInput.value) {
				const input = (searchInput.value as any).$el.querySelector("input");

				if (input) {
					input.value = cleaned;
				}
			}
		});
	}

	// TODO: Need to refactory this and bring in the filter into this component to be reusable
	function toggleFilters() {
		filtersVisible.value = !filtersVisible.value;
		emits("toggleFilters", filtersVisible.value);
		console.log("toggleFilters", filtersVisible.value);
	}

	async function onSearchPaste(event: ClipboardEvent) {
		if (!handleSearchPaste(event)) {
			const message = "Pasted text contained invalid characters. Please remove any special characters and try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
		}
	}
</script>
